import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackHeapEvent } from 'utils/analytics';
import ListOnKlarityHealth1 from './Step1';
import ListOnKlarityHealth2 from './Step2';
import ListOnKlarityHealth3 from './Step3';
import ListOnKlarityHealth4 from './Step4';
import ListOnKlarityHealth5 from './Step5';
import { Button, message, Modal } from 'antd';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { getNextStep, getPrevStep } from 'pages/PPAOnboardingSteps/utils';
import { EPPAStep } from 'types/ppaOnboarding';
import { savePPAOnboardingBasicInfo, savePPAPaymentMethod, savePPAPersonalIdentification, saveStep } from 'api/ppaOnboarding';
import { IReviewData } from 'hooks/usePPAOnboardingReviewInfo';

interface IProps {
    setShowProgress: (show: boolean) => void;
    step?: EPPAStep;
    setStep: (step: EPPAStep) => void;
    reviewData: IReviewData;
    refetchReviewData?: () => void;
}

const formIds = {
    [EPPAStep.STEP_11]: 'personalID',
    [EPPAStep.STEP_12]: 'licenseInfo',
    [EPPAStep.STEP_13]: 'paymentInfo',
};

const ListOnKlarityHealth = ({ setShowProgress, step, setStep, reviewData, refetchReviewData }: IProps) => {
    const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
    // Step5(STEP_13) 注册的 Next 拦截器：case 3（有卡未 list）时接管 Next，弹 without-payment 弹窗
    const nextInterceptRef = useRef<(() => boolean) | null>(null);
    const [formId, setFormId] = useState<string>();
    const [submitLoading, setSubmitLoading] = useState(false);
    const [showProListingsModal, setShowProListingsModal] = useState(false);
    const [showLearnMore, setShowLearnMore] = useState(false);

    useEffect(() => {
        setFormId(formIds[step as keyof typeof formIds]);
    }, [step]);

    useEffect(() => {
        setShowProgress(true);
    }, []);

    const handleBack = useCallback(() => {
        if (step) {
            trackHeapEvent('WebApp - PPA Onboarding - ListOnKlarityHealth - Click - Back', {
                step,
            });
            const newStep = getPrevStep(step);
            setStep(newStep);
        } else {
            console.error('step is undefined');
        }
    }, [setStep, step]);

    const proceedToNextStep = useCallback(async (fromStep: EPPAStep) => {
        try {
            setSubmitLoading(true);
            const nextStep = getNextStep(fromStep);
            const res = await saveStep(nextStep);
            if (res && !res.error) {
                trackHeapEvent('WebApp - PPA Onboarding - ListOnKlarityHealth - Click - Next Success', {
                    fromStep,
                    toStep: nextStep,
                });
                setStep(nextStep);
                refetchReviewData?.();
            } else {
                trackHeapEvent('WebApp - PPA Onboarding - ListOnKlarityHealth - View - Next Failed', {
                    step: fromStep,
                    error: res?.error,
                });
                message.error(res?.error);
            }
        } catch (error) {
            trackHeapEvent('WebApp - PPA Onboarding - ListOnKlarityHealth - View - Next Error', {
                step: fromStep,
                error: error instanceof Error ? error.message : 'Unknown error',
            });
            console.error(error);
        } finally {
            setSubmitLoading(false);
        }
    }, [setStep, refetchReviewData]);

    const handleSaveStep = useCallback(async () => {
        if (!step) {
            console.error('step is undefined');
            return;
        }

        // Check if we're moving from STEP_13, show modal instead
        if (step === EPPAStep.STEP_13) {
            // case 3（有卡未 list）：Step5 接管 Next，弹 without-payment 弹窗，不进 Pro-Listings
            if (nextInterceptRef.current?.()) {
                return;
            }
            // 显示模态框，阻止跳转
            setShowProListingsModal(true);
            return; // 阻止跳转，只显示模态框
        }

        // 所有步骤（包括 STEP_11 和 STEP_12）的跳过逻辑一致：直接跳转到下一步
        await proceedToNextStep(step);
    }, [step, proceedToNextStep]);

    const handleNext = useCallback(async (data: any, stepVal: EPPAStep) => {
        try {
            setSubmitLoading(true);
            trackHeapEvent('WebApp - PPA Onboarding - ListOnKlarityHealth - Click - Next Attempt', {
                step: stepVal,
            });
            let res = null;
            if (stepVal === EPPAStep.STEP_11) {
                res = await savePPAPersonalIdentification(data);
            } else if (stepVal === EPPAStep.STEP_7) {
                res = await savePPAPaymentMethod(data.supportPayType);
            } else {
                res = await savePPAOnboardingBasicInfo(data);
            }
            if (res && !res.error) {
                trackHeapEvent('WebApp - PPA Onboarding - ListOnKlarityHealth - View - Next Success', {
                    step: stepVal,
                });
                handleSaveStep();
            } else {
                trackHeapEvent('WebApp - PPA Onboarding - ListOnKlarityHealth - View - Next Failed', {
                    step: stepVal,
                    error: res?.error,
                });
                message.error(res?.error);
            }
        } catch (error) {
            trackHeapEvent('WebApp - PPA Onboarding - ListOnKlarityHealth - View - Next Error', {
                step: stepVal,
                error: error instanceof Error ? error.message : 'Unknown error',
            });
            console.error(error);
        } finally {
            setSubmitLoading(false);
        }
    }, [handleSaveStep, setSubmitLoading]);

    const commonProps = useMemo(() => {
        return {
            setNextButtonDisabled,
            onNext: handleNext,
        };
    }, [handleNext]);

    const contentRender = useMemo(() => {
        let content = null;

        if (step === EPPAStep.STEP_9) {
            content = <ListOnKlarityHealth1 />;
        } else if (step === EPPAStep.STEP_10) {
            content = <ListOnKlarityHealth2 />;
        } else if (step === EPPAStep.STEP_11) {
            content = <ListOnKlarityHealth3 initValue={reviewData.identification} {...commonProps} onSaveStep={handleSaveStep} formId={formId as string} />;
        } else if (step === EPPAStep.STEP_12) {
            content = <ListOnKlarityHealth4 setNextButtonDisabled={setNextButtonDisabled} initValue={reviewData.licenses} onSaveStep={handleSaveStep} formId={formId as string} />;
        } else if (step === EPPAStep.STEP_13) {
            content = (
                <ListOnKlarityHealth5
                    {...commonProps}
                    nextInterceptRef={nextInterceptRef}
                    onListedWithoutPayment={() => setShowProListingsModal(true)}
                />
            );
        }

        return content;
    }, [commonProps, step, formId, handleSaveStep, reviewData]);

    const footerBtnsRender = useMemo(() => {
        if (step === EPPAStep.STEP_9 || step === EPPAStep.STEP_10) {
            return (
                <>
                    <Button onClick={() => {
                        setStep(getPrevStep(step as EPPAStep));
                    }}
                    >Back
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => {
                            handleSaveStep();
                        }}
                    >Next
                    </Button>
                </>
            );
        } else if (step === EPPAStep.STEP_11 || step === EPPAStep.STEP_12) {
            return (
                <>
                    <Button onClick={handleBack}>Back</Button>
                    <div>
                        <Button type="primary" htmlType="submit" form={formId} disabled={nextButtonDisabled} loading={submitLoading}>Next</Button>
                        <Button type="text" onClick={handleSaveStep} className={s.skipBtn}>Skip for now</Button>
                    </div>
                </>
            );
        } else if (step === EPPAStep.STEP_13) {
            return (
                <>
                    <Button onClick={handleBack}>Back</Button>
                    <Button type="primary" disabled={nextButtonDisabled} onClick={handleSaveStep} loading={submitLoading}>Next</Button>
                </>
            );
        } else {
            return (
                <>
                    <Button onClick={handleBack}>Back</Button>
                    <Button type="primary" htmlType="submit" form={formId} disabled={nextButtonDisabled} loading={submitLoading}>Next</Button>
                </>
            );
        }
    }, [step, setStep, handleSaveStep, handleBack, formId, nextButtonDisabled, submitLoading]);

    const handleAcceptCredits = useCallback(async () => {
        setShowProListingsModal(false);
        setShowLearnMore(false);
        if (step === EPPAStep.STEP_13) {
            trackHeapEvent('WebApp - PPA Onboarding - ListOnKlarityHealth - Click - Accept Pro-Listings Credits', {
                step,
            });
            await proceedToNextStep(step);
        }
    }, [step, proceedToNextStep]);

    const handleSkipCredits = useCallback(async () => {
        setShowProListingsModal(false);
        setShowLearnMore(false);
        if (step === EPPAStep.STEP_13) {
            trackHeapEvent('WebApp - PPA Onboarding - ListOnKlarityHealth - Click - Skip Pro-Listings Credits', {
                step,
            });
            await proceedToNextStep(step);
        }
    }, [step, proceedToNextStep]);

    const handleCloseModal = useCallback(async () => {
        setShowProListingsModal(false);
        setShowLearnMore(false);
        if (step === EPPAStep.STEP_13) {
            trackHeapEvent('WebApp - PPA Onboarding - ListOnKlarityHealth - Click - Close Pro-Listings Modal', {
                step,
            });
            await proceedToNextStep(step);
        }
    }, [step, proceedToNextStep]);

    return (
        <div className={s.wrap}>
            {contentRender}
            <div className={s.footer}>
                {
                    footerBtnsRender
                }
            </div>
            <Modal
                width="480px"
                open={showProListingsModal}
                className={`${commonS.modalFixHeightWrap} ${s.proListingsModal}`}
                footer={null}
                onCancel={handleCloseModal}
                closable
                maskClosable
                destroyOnClose
            >
                <div className={s.proListingsModalContent}>
                    <div className={s.proListingsHeader}>
                        <div className={s.proListingsTitle}>
                            🎉 You&apos;re qualified for <span className={s.highlight}>$150</span> in Pro-Listings<span className={s.highlight}> free credits</span>
                        </div>
                        <p className={s.proListingsDescription}>
                            Enjoy $150 in free credits for Pro-Listings to appear at the top of search results and reach more patients. Accept now to continue advanced setup and launch your first campaign.
                        </p>
                        <div className={s.learnMoreSection}>
                            <div
                                className={s.learnMoreLink}
                                onClick={() => setShowLearnMore(!showLearnMore)}
                            >
                                <span>Learn more</span>
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={showLearnMore ? s.chevronRotated : ''}
                                >
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="#0E62F4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            {showLearnMore && (
                                <p className={s.learnMoreContent}>
                                    Pro-Listings is Klarity&apos;s pay-for-results program that helps your profile stand out in patient searches, making it easier for new patients to find and book with you.
                                </p>
                            )}
                        </div>
                    </div>
                    <div className={s.proListingsActions}>
                        <Button
                            type="primary"
                            onClick={handleAcceptCredits}
                            className={s.acceptButton}
                            loading={submitLoading}
                        >
                            I accept my free credits
                        </Button>
                        <Button
                            onClick={handleSkipCredits}
                            className={s.skipButton}
                            loading={submitLoading}
                            type="text"
                        >
                            Skip for now
                        </Button>
                        <p className={s.proListingsFooter}>
                            By accepting, you agree that credits apply automatically to your first Pro-Listings campaign. Credits expire in 1 month.
                        </p>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ListOnKlarityHealth;
